/**
 * Envio de eventos do Meta Pixel para UM pixel por vez (20/10/2026).
 *
 * Por que existe: `fbq('track')` manda o evento para TODOS os pixels já
 * iniciados na página. No checkout convivem o pixel da plataforma
 * (/integrations/fbpixel), o do builder (settings.tracking) e, no
 * auto-checkout, o da Curseduca — cada um recebia os eventos dos outros:
 * Purchase em dobro, InitiateCheckout em dobro.
 *
 * Regras:
 * 1. Sempre `trackSingle` para o pixel de quem dispara.
 * 2. Purchase leva `eventID` = referência da compra, a mesma usada pelo
 *    obrigado do front-member e pelo pixel do builder: a Meta deduplica o que
 *    chegar repetido de outra página/origem.
 * 3. Purchase sai uma vez por pixel e por compra neste navegador
 *    (localStorage): recarregar a tela de espera do Pix não reconta.
 * 4. InitiateCheckout sai uma vez por pixel por carregamento de página.
 */
import ReactPixel from 'react-facebook-pixel';

const PURCHASE_PREFIX = 'waid-pixel-purchase:';

const initializedPixels = new Set<string>();
const initiatedPixels = new Set<string>();

/** `fbq('init')` uma vez por pixel — compartilhado com o pixel do builder. */
export const ensurePixelInitialized = (pixelId: string) => {
	if (initializedPixels.has(pixelId)) return;
	ReactPixel.init(pixelId);
	initializedPixels.add(pixelId);
};

export const trackOnPixel = (
	pixelId: string,
	event: string,
	data?: unknown,
	eventID?: string
) => {
	ensurePixelInitialized(pixelId);
	ReactPixel.fbq(
		'trackSingle',
		pixelId,
		event,
		data ?? {},
		...(eventID ? [{ eventID }] : [])
	);
};

/** Um InitiateCheckout por pixel por carregamento de página. */
export const trackInitiateCheckoutOnPixel = (
	pixelId: string,
	data?: unknown
) => {
	if (initiatedPixels.has(pixelId)) return;
	initiatedPixels.add(pixelId);
	trackOnPixel(pixelId, 'InitiateCheckout', data);
};

const wasFired = (key: string): boolean => {
	try {
		return localStorage.getItem(key) === '1';
	} catch {
		// Sem storage não há como deduplicar aqui; o eventID ainda deduplica na Meta.
		return false;
	}
};

const markFired = (key: string) => {
	try {
		localStorage.setItem(key, '1');
	} catch {
		// segue sem persistir
	}
};

/**
 * Purchase de uma compra JÁ PAGA. Quem chama garante a aprovação (cartão
 * aprovado na hora, Pix confirmado); Pix e boleto recém-gerados não chamam.
 */
export const trackPurchaseOnPixel = (
	pixelId: string,
	{ value, reference }: { value: number; reference?: string | null }
) => {
	const data = { currency: 'BRL', value };

	if (!reference) {
		trackOnPixel(pixelId, 'Purchase', data);
		return;
	}

	const key = `${PURCHASE_PREFIX}${pixelId}:${reference}`;
	if (wasFired(key)) return;
	trackOnPixel(pixelId, 'Purchase', data, reference);
	markFired(key);
};

/** Só para testes: zera o estado de módulo entre casos. */
export const __resetPixelEventsForTests = () => {
	initializedPixels.clear();
	initiatedPixels.clear();
};
